import { useState } from "react";
import type { MentorListItem } from "@/lib/mentors/types";
import SignedImage from "@/components/media/SignedImage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MentorBookingCalendar } from "./MentorBookingCalendar";

interface Props {
  mentor: MentorListItem;
  onRequest: (message: string) => Promise<{ error?: string } | void>;
  busy?: boolean;
}

const formatRate = (cents: number | null, currency: string | null) => {
  if (!cents) return "Rate on request";
  const ccy = (currency ?? "USD").toUpperCase();
  return `${(cents / 100).toLocaleString(undefined, {
    style: "currency",
    currency: ccy,
    maximumFractionDigits: 0,
  })} / hr`;
};

const MentorCard = ({ mentor, onRequest, busy }: Props) => {
  const [open, setOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const res = await onRequest(msg.trim());
    if (res && "error" in res && res.error) {
      setErr(res.error);
      return;
    }
    setSent(true);
    setMsg("");
  };

  const name = mentor.display_name ?? mentor.username ?? "Mentor";

  return (
    <article className="rounded-2xl border border-border/60 bg-card p-5 flex flex-col gap-4">
      <div className="flex items-start gap-4">
        {mentor.avatar_url ? (
          <SignedImage
            src={mentor.avatar_url}
            alt={name}
            className="h-14 w-14 rounded-full object-cover"
          />
        ) : (
          <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center text-lg font-semibold text-muted-foreground">
            {name.slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold truncate">{name}</h3>
          {mentor.headline ? (
            <p className="text-sm text-muted-foreground line-clamp-2">{mentor.headline}</p>
          ) : null}
          <p className="mt-1 text-xs font-medium text-foreground">
            {formatRate(mentor.hourly_rate_cents, mentor.currency)}
          </p>
        </div>
      </div>

      {mentor.bio ? (
        <p className="text-sm text-muted-foreground line-clamp-3">{mentor.bio}</p>
      ) : null}

      {mentor.specialties && mentor.specialties.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {mentor.specialties.slice(0, 6).map((s) => (
            <span
              key={s}
              className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
            >
              {s}
            </span>
          ))}
        </div>
      ) : null}

      {!open ? (
        <div className="mt-auto flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex-1 rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium hover:opacity-90 transition-opacity"
          >
            Request mentor
          </button>
          <button
            type="button"
            onClick={() => setBookOpen(true)}
            className="flex-1 rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-muted transition-colors"
          >
            Book a slot
          </button>
        </div>
      ) : sent ? (
        <p className="text-xs text-emerald-600 dark:text-emerald-400">
          Request sent. They'll be notified.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="A short note about what you'd like help with…"
            rows={3}
            className="w-full rounded-lg border border-border bg-background p-2 text-sm"
            maxLength={500}
            required
          />
          {err ? <p className="text-xs text-destructive">{err}</p> : null}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send request"}
            </button>
          </div>
        </form>
      )}

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book a slot with {mentor.display_name ?? "this mentor"}</DialogTitle>
          </DialogHeader>
          <MentorBookingCalendar
            mentorUserId={mentor.user_id}
            app={String(mentor.app)}
          />
        </DialogContent>
      </Dialog>
    </article>
  );
};

export default MentorCard;
